import type { Company } from './Companies';
import type { CargoType } from './CargoTypes';
import type { OperationType } from './OperationTypes';
import type { OrderType } from './OrderTypes';
import type { VehicleType } from './VehicleTypes';
import type { Carrier } from './Carriers';
import type { Warehouse } from './Warehouses';
import type { Dock } from './Docks';
import type { CancelationReason } from './CancelationReasons';
import type { OrderItem } from './OrderItems';
import { User } from './Users';
import { OrderFile } from './OrderFiles';
import { OrderReader } from './OrderReaders';

export type OrderStatus =
  | 'aguardando'
  | 'chegada'
  | 'iniciado'
  | 'finalizado'
  | 'liberado'
  | 'noShow'
  | 'cancelado'
  | 'atrasado'
  | 'todos';

export type OrderActionOrigin = 'WEB' | 'APP';

export type OrderActionType =
  | 'VEHICLE_ARRIVED'
  | 'STARTED'
  | 'FINISHED'
  | 'VEHICLE_DEPARTED'
  | 'NO_SHOW'
  | 'CANCELLATION';

export interface OrderActionLog {
  id: number;
  actionType: OrderActionType;
  origin: OrderActionOrigin;
  performedAt: string;
  createdAt: string;
  user?: Pick<User, 'id' | 'name' | 'email' | 'type'>;
}

export type OrderActionTimestampField =
  | 'vehicleArrivedAt'
  | 'startedAt'
  | 'finishedAt'
  | 'vehicleDepartedAt'
  | 'noShowAt'
  | 'canceledAt';

//Usa para mapear os campos do front para o back
export const orderActionTypeByTimestampField: Record<
  OrderActionTimestampField,
  OrderActionType
> = {
  vehicleArrivedAt: 'VEHICLE_ARRIVED',
  startedAt: 'STARTED',
  finishedAt: 'FINISHED',
  vehicleDepartedAt: 'VEHICLE_DEPARTED',
  noShowAt: 'NO_SHOW',
  canceledAt: 'CANCELLATION',
};

// Representa um objeto indexado pelo actionType. Como nem toda ordem possui log
// para todas as etapas, usamos Partial para permitir chaves ausentes.
export type LatestOrderActionLogsByType = Partial<
  Record<OrderActionType, OrderActionLog>
>;

// Agrupa os logs por actionType e mantem apenas o registro mais recente de cada
// etapa. O criterio de "mais recente" e o maior id, conforme definido no card.
export const getLatestOrderActionLogByType = (
  // Quando actionLogs vier undefined, usamos array vazio para evitar erro no reduce.
  actionLogs: OrderActionLog[] = []
): LatestOrderActionLogsByType =>
  // Percorre todos os logs da ordem e monta um acumulador no formato:
  // { VEHICLE_ARRIVED: log, STARTED: log, ... }
  actionLogs.reduce<LatestOrderActionLogsByType>((acc, log) => {
    // Busca o log que ja foi salvo para este actionType, se existir.
    const current = acc[log.actionType];

    // Se ainda nao existe log para a etapa, ou se o log atual tem id maior,
    // substitui o valor salvo no acumulador.
    if (!current || log.id > current.id) {
      acc[log.actionType] = log;
    }

    // Retorna o acumulador para a proxima iteracao do reduce.
    return acc;
  }, {});

// Cria o nome personalizado para exibir no excel
export const formatOrderActionAuthor = (log?: OrderActionLog): string => {

  if (!log?.user?.name) return '';

  const origin = log.origin === 'WEB' ? 'Web' : 'App';

  return `${log.user.name} (${origin})`;
};

export interface Order {
  id: number;
  horaCertaAppointmentNumber?: string | null;
  carrierId: number;
  companyId: number;
  warehouseId: number;
  orderTypeId: number;
  operationTypeId: number;
  dockId: number;
  // parent relationships
  orderType: Pick<OrderType, 'id' | 'name'>;
  operationType: Pick<OperationType, 'id' | 'name'>;
  vehicleType: Pick<VehicleType, 'id' | 'name'>;
  cargoType: Pick<CargoType, 'id' | 'name'>;
  company: Pick<Company, 'id' | 'tradeName' | 'logoUrl'>;
  carrier: Pick<Carrier, 'id' | 'tradeName'>;
  cellPhone?:string;
  warehouse: Pick<
    Warehouse,
    | 'id'
    | 'tradeName'
    | 'addressStreet'
    | 'addressNumber'
    | 'addressComplement'
    | 'addressNeighborhood'
    | 'addressCity'
    | 'addressState'
    | 'addressZipcode'
    | 'addressUf'
    | 'countryId'
    | 'country'
  >;
  dock: Pick<Dock, 'id' | 'name'>;
  // attributes
  scheduledAt: string;
  weightCapacity: number;
  duration: number;
  driverName: string;
  driverDocument: string;
  vehiclePlate: string;
  // the following depend on the operation type
  devolutionNumber: string | null;
  exportationSecuritySeal: string | null;
  exportationContainer: string | null;
  importationOrder: string | null;
  importationSequential: string | null;
  importationContainer: string | null;
  importationDeclaration: string | null;
  // just for reference
  observation: string | null;
  material: string | null;
  // datetime for arrival and departure of vehicle
  vehicleArrivedAt: string | null;
  vehicleDepartedAt: string | null;
  // datetime for the start and end of the operation
  startedAt: string | null;
  finishedAt: string | null;
  // when the order was toggled as no-show
  noShowAt: string | null;
  // cancelation
  canceledAt: string | null;
  cancelationReason: Pick<CancelationReason, 'id' | 'name'> | null;
  // timestamps
  createdAt: string;
  updatedAt: string;

  createdByUser: Pick<User, 'email' | 'id' | 'name' | 'type'>;
  // order items
  orderItems: OrderItem[];
  orderFiles?: OrderFile[];
  //readers
  orderReader: OrderReader;
  // sum of each order item weight
  totalWeight: number;
  // Value in BRL
  totalValue: number;
  // status
  status: OrderStatus;
  statusName?: OrderStatus;
  actionLogs?: OrderActionLog[];
}

export interface UploadOrderEvent
  extends Partial<
    Pick<
      Order,
      'vehicleArrivedAt' | 'startedAt' | 'vehicleDepartedAt' | 'finishedAt'
    >
  > {}

export interface PaginatedOrder extends Order {
  physicalDocks: Array<{
    id: number;
    number: number;
    name: string;
    areaName: string;
  }>;
}
